import {
  ApolloClient,
  ApolloLink,
  ApolloProvider as BaseApolloProvider,
  InMemoryCache,
} from "@apollo/client";
import type { NormalizedCacheObject, Operation } from "@apollo/client";
import { RetryLink } from "@apollo/client/link/retry";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import type { ApolloProviderProps } from "@apollo/client/react/context";
import {
  getMainDefinition,
  relayStylePagination,
} from "@apollo/client/utilities";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { Kind, OperationTypeNode } from "graphql";
import type { FragmentDefinitionNode, OperationDefinitionNode } from "graphql";
import { createClient } from "graphql-ws";
import { get, isUndefined } from "lodash";
import { createElement, useMemo } from "react";
import { useNavigate } from "react-router";

import { errorLink } from "./error-link";

import { getEnvironment } from "utils/environment";

const getCookie = (name: string): string => {
  if (document.cookie !== "") {
    const cookies: string[] = document.cookie.split(";");
    const cookieValue: string | undefined = cookies.find(
      (cookie: string): boolean => cookie.trim().startsWith(`${name}=`),
    );
    if (!isUndefined(cookieValue)) {
      return decodeURIComponent(cookieValue.trim().substring(name.length + 1));
    }
  }

  return "";
};

const httpLink = createUploadLink({
  credentials: "same-origin",
  headers: {
    "X-CSRFToken": getCookie("csrftoken"),
    accept: "application/json",
  },
  uri: `${window.location.origin}/api`,
});

const wsLink = new GraphQLWsLink(
  createClient({
    connectionAckWaitTimeout: Infinity,
    keepAlive: 10000,
    shouldRetry: (): boolean => true,
    url: `wss://${window.location.host}/api`,
  }),
);

const apiLink: ApolloLink = ApolloLink.split(
  ({ query }: Operation): boolean => {
    const definition: FragmentDefinitionNode | OperationDefinitionNode =
      getMainDefinition(query);

    return (
      definition.kind === Kind.OPERATION_DEFINITION &&
      definition.operation === OperationTypeNode.SUBSCRIPTION
    );
  },
  wsLink,
  httpLink,
);

const MAX_QUERY_ATTEMPTS = 4;
const MAX_MUTATION_ATTEMPTS = 3;
const BAD_GATEWAY = 502;
const SERVICE_UNAVAILABLE = 503;
/**
 * A 504 already cost the gateway timeout, and a mutation that got a 502 may
 * have run on the backend, so neither is retried.
 */
const QUERY_RETRY_STATUSES: ReadonlySet<number> = new Set([
  BAD_GATEWAY,
  SERVICE_UNAVAILABLE,
]);
const MUTATION_RETRY_STATUSES: ReadonlySet<number> = new Set([
  SERVICE_UNAVAILABLE,
]);
const isTransientError = (
  error: Error,
  retryStatuses: ReadonlySet<number>,
): boolean => {
  const { statusCode } = error as { statusCode?: number };

  return statusCode === undefined || retryStatuses.has(statusCode);
};

const shouldRetryOperation = (
  count: number,
  operation: Operation,
  error: Error | undefined,
): boolean => {
  if (error === undefined) {
    return false;
  }
  if (operation.getContext().skipRetry === true) {
    return false;
  }
  if (
    operation.query.definitions.length > 0 &&
    String(get(operation.query.definitions[0], "operation", "query")) ===
      "mutation"
  ) {
    return (
      isTransientError(error, MUTATION_RETRY_STATUSES) &&
      count <= MAX_MUTATION_ATTEMPTS
    );
  }

  return (
    isTransientError(error, QUERY_RETRY_STATUSES) && count <= MAX_QUERY_ATTEMPTS
  );
};

const retryLink: ApolloLink = new RetryLink({
  attempts: shouldRetryOperation,
  delay: {
    initial: 300,
    jitter: true,
    max: 5000,
  },
});

/**
 * Load cache with union type definitions
 * @see https://www.apollographql.com/docs/react/v3/data/fragments#using-fragments-with-unions-and-interfaces
 */
const getCache = (): InMemoryCache =>
  new InMemoryCache({
    possibleTypes: {
      Root: ["GitRoot", "IPRoot", "URLRoot"],
    },
    typePolicies: {
      Asset: {
        fields: {
          components: relayStylePagination(),
        },
        merge: true,
      },
      Finding: {
        fields: {
          vulnStats: { merge: true },
          vulnerabilities: relayStylePagination(["filters"]),
          zeroRiskConnection: relayStylePagination(),
        },
        keyFields: ["id"],
      },
      Flow: { keyFields: false },
      ForcesExecution: {
        fields: {
          vulnerabilitiesConnection: relayStylePagination([
            "filters",
            "sortBy",
          ]),
        },
        keyFields: ["executionId"],
      },
      GitEnvironmentUrl: {
        keyFields: ["id", "rootId"],
      },
      Group: {
        fields: {
          authors: relayStylePagination(["date", "filters", "sortBy"]),
          events: relayStylePagination(),
          executionsConnection: relayStylePagination([
            "filters",
            "sortBy",
            "type",
          ]),
          findingStats: { merge: true },
          flows: relayStylePagination(["filters", "search", "sortBy"]),
          gitRoots: relayStylePagination(["filters", "sortBy"]),
          ipRoots: relayStylePagination(),
          package: {
            merge(_existing, incoming): unknown {
              return incoming;
            },
          },
          stakeholders: relayStylePagination([
            "filters",
            ["registrationStatus", "role", "search"],
          ]),
          toeInputs: relayStylePagination(),
          toeLines: relayStylePagination(),
          toePackageStats: { merge: true },
          toePackages: relayStylePagination(),
          urlRoots: relayStylePagination(),
          vulnerabilities: relayStylePagination(["filters", "sortBy"]),
        },
        keyFields: ["name"],
      },
      Me: {
        fields: {
          drafts: relayStylePagination(),
          findingEmptyEvidence: relayStylePagination(),
          findingEvidenceDrafts: relayStylePagination(),
          findingReattacks: relayStylePagination(),
          groups: relayStylePagination(),
          organizations: {
            merge(_existing, incoming): unknown {
              return incoming;
            },
          },
          pendingEvents: relayStylePagination(["filters", "sortBy"]),
          stats: {
            merge: true,
          },
          trial: {
            merge: true,
          },
          vulnerabilitiesConnectionAssigned: relayStylePagination(),
          vulnerabilitiesReattacks: relayStylePagination(),
          vulnerabilitiesSeverityRequests: relayStylePagination(),
        },
        keyFields: ["userEmail"],
      },
      Organization: {
        fields: {
          assetsConnection: relayStylePagination(["filters", "sortBy"]),
          authors: relayStylePagination(["filters", "sortBy"]),
          billing: { merge: true },
          groupsConnection: relayStylePagination(["date", "filters", "sortBy"]),
          integrationRepositoriesConnection: relayStylePagination(),
          scmConnections: { merge: false },
          sourcesConnection: relayStylePagination(["filters", "sortBy"]),
          stakeholders: relayStylePagination(),
          ztnaHttpLogs: relayStylePagination(),
          ztnaNetworkLogs: relayStylePagination(),
          ztnaSessionLogs: relayStylePagination(),
        },
        keyFields: ["name"],
      },
      OrganizationBilling: {
        fields: {
          authorsConnection: relayStylePagination(["filters", "sortBy"]),
        },
        keyFields: false,
      },
      Query: {
        fields: {
          group: {
            merge(_existing, incoming): unknown {
              return incoming;
            },
          },
          organization: {
            merge(_existing, incoming): unknown {
              return incoming;
            },
          },
          organizationId: {
            merge(_existing, incoming): unknown {
              return incoming;
            },
          },
        },
      },
      ToePackage: {
        fields: {
          locations: {
            merge(_existing, incoming): unknown {
              return incoming;
            },
          },
        },
      },
      Vulnerability: {
        fields: {
          tracking: relayStylePagination(),
        },
        keyFields: ["id"],
      },
    },
  });

type TProviderProps = Omit<ApolloProviderProps<unknown>, "client">;

const ApolloProvider: React.FC<TProviderProps> = (
  props: TProviderProps,
): JSX.Element => {
  const navigate = useNavigate();
  const client = useMemo((): ApolloClient<NormalizedCacheObject> => {
    return new ApolloClient({
      cache: getCache(),
      connectToDevTools: getEnvironment() !== "production",
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "cache-and-network",
        },
      },
      link: ApolloLink.from([errorLink(navigate), retryLink, apiLink]),
    });
    // This computed value will never change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return createElement(BaseApolloProvider, { client, ...props });
};

export { ApolloProvider, getCache, getCookie, shouldRetryOperation };
